import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';
import { AuthService } from 'app/@core/services/auth.service';

export interface UserNotification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  body: string | null;
  data: Record<string, any> | null;
  read_at: string | null;
  deleted_at: string | null;
  created_at: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationsService implements OnDestroy {
  private readonly _apiUrl = `${environment.baseUrl}/api/notifications`;

  private _notifications$ = new BehaviorSubject<UserNotification[]>([]);
  private _unreadCount$ = new BehaviorSubject<number>(0);
  private _eventSource: EventSource | null = null;
  private _reconnectTimer: any = null;
  private _titleInterval: any = null;
  private _originalTitle = document.title;

  readonly notifications$ = this._notifications$.asObservable();
  readonly unreadCount$ = this._unreadCount$.asObservable();

  constructor(
    private _http: HttpClient,
    private _authService: AuthService,
    private _zone: NgZone,
    private _translateService: TranslateService,
  ) {}

  connect(): void {
    // Non aprire se c'è già una connessione attiva (OPEN=0 o CONNECTING=0)
    if (this._eventSource && this._eventSource.readyState !== EventSource.CLOSED) return;

    this._eventSource?.close();
    this._eventSource = null;

    this.loadNotifications();

    const rawToken = this._authService.currentTokenValue;
    const token = rawToken ? rawToken.replace(/^"|"$/g, '') : null;
    if (!token) return;

    const url = `${environment.baseUrl}/api/notifications/stream?token=${encodeURIComponent(token)}`;
    this._eventSource = new EventSource(url);

    this._eventSource.addEventListener('notification', (e: MessageEvent) => {
      this._zone.run(() => {
        try {
          const notif: UserNotification = JSON.parse(e.data);
          this._notifications$.next([notif, ...this._notifications$.getValue()]);
          const newCount = this._unreadCount$.getValue() + 1;
          this._unreadCount$.next(newCount);
          this._startTitleBlink(newCount);
        } catch { /* ignore */ }
      });
    });

    this._eventSource.onerror = () => {
      // Se la connessione è caduta definitivamente (CLOSED) riprova dopo 5 secondi
      if (this._eventSource?.readyState === EventSource.CLOSED) {
        this._eventSource = null;
        if (this._reconnectTimer) return;
        this._reconnectTimer = setTimeout(() => {
          this._reconnectTimer = null;
          this.connect();
        }, 5000);
      }
    };
  }

  disconnect(): void {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._eventSource?.close();
    this._eventSource = null;
    this._stopTitleBlink();
  }

  loadNotifications(): void {
    this._http.get<{ success: boolean; data: UserNotification[]; unread_count: number }>(
      `${this._apiUrl}?limit=30`
    ).subscribe({
      next: (res) => {
        this._notifications$.next(res.data ?? []);
        const count = res.unread_count ?? 0;
        this._unreadCount$.next(count);
        if (count > 0) this._startTitleBlink(count);
      },
      error: () => {},
    });
  }

  loadAll(): Observable<UserNotification[]> {
    return this._http.get<{ success: boolean; data: UserNotification[] }>(
      `${this._apiUrl}?limit=200&include_deleted=1`
    ).pipe(map(res => res.data ?? []));
  }

  markRead(id: string): Observable<void> {
    return this._http.put<void>(`${this._apiUrl}/${id}/read`, {}).pipe(
      map(() => {
        const updated = this._notifications$.getValue().map(n =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n
        );
        this._notifications$.next(updated);
        const newCount = Math.max(0, this._unreadCount$.getValue() - 1);
        this._unreadCount$.next(newCount);
        if (newCount === 0) this._stopTitleBlink();
      })
    );
  }

  deleteNotification(id: string): Observable<void> {
    return this._http.delete<void>(`${this._apiUrl}/${id}`).pipe(
      map(() => {
        const current = this._notifications$.getValue();
        const target = current.find(n => n.id === id);
        this._notifications$.next(current.filter(n => n.id !== id));
        if (target && !target.read_at) {
          const newCount = Math.max(0, this._unreadCount$.getValue() - 1);
          this._unreadCount$.next(newCount);
          if (newCount === 0) this._stopTitleBlink();
        }
      })
    );
  }

  markAllRead(): Observable<void> {
    return this._http.put<void>(`${this._apiUrl}/read-all`, {}).pipe(
      map(() => {
        const updated = this._notifications$.getValue().map(n => ({ ...n, read_at: new Date().toISOString() }));
        this._notifications$.next(updated);
        this._unreadCount$.next(0);
        this._stopTitleBlink();
      })
    );
  }

  private _startTitleBlink(count: number): void {
    if (this._titleInterval) return;
    let toggle = false;
    this._titleInterval = setInterval(() => {
      document.title = toggle
        ? this._originalTitle
        : `(${count}) Nuova notifica`;
      toggle = !toggle;
    }, 1200);
  }

  private _stopTitleBlink(): void {
    if (this._titleInterval) {
      clearInterval(this._titleInterval);
      this._titleInterval = null;
    }
    document.title = this._originalTitle;
  }

  localizeBody(body?: string | null): string {
    if (!body) return '';
    return this._translateService.instant(body);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
